import { useT } from '@/i18n/useT';
import { BOTTOM_TABS, useUiStore, type BottomTab } from '@/store/uiStore';
import { EmptyState } from '@/ui/components/EmptyState';
import { Panel } from '@/ui/components/Panel';
import { Tabs, type TabItem } from '@/ui/components/Tabs';
import { tabId, tabPanelId } from '@/ui/components/tabIds';
import { BOTTOM_TABS_PREFIX } from '@/ui/tabs/ids';
import { useLadderEditorStore } from '@/ui/editor/ladder/store/ladderEditorStore';
import { IOPanel } from './IOPanel';
import { AlarmPanel } from './AlarmPanel';
import { ModbusMonitorPanel } from './ModbusMonitorPanel';
import styles from './BottomPanel.module.css';

/**
 * Bottom panel with its tab system. Each tab's content is a placeholder in Phase 1; adding a real
 * view later means rendering it instead of the `EmptyState` for that tab id.
 */
export function BottomPanel() {
  const t = useT();
  const activeTab = useUiStore((state) => state.activeBottomTab);
  const setBottomTab = useUiStore((state) => state.setBottomTab);
  const height = useUiStore((state) => state.panelSizes.bottom);
  const diagnostics = useLadderEditorStore((state) => state.diagnostics);

  const tabs: readonly TabItem<BottomTab>[] = BOTTOM_TABS.map((tab) => ({
    id: tab,
    label: t(`bottom.tab.${tab}`),
  }));

  return (
    <Panel label={t('bottom.label')} className={styles.panel} style={{ height }}>
      <Tabs
        label={t('bottom.tabs.label')}
        idPrefix={BOTTOM_TABS_PREFIX}
        tabs={tabs}
        activeId={activeTab}
        onChange={setBottomTab}
      />
      <div
        role="tabpanel"
        id={tabPanelId(BOTTOM_TABS_PREFIX, activeTab)}
        aria-labelledby={tabId(BOTTOM_TABS_PREFIX, activeTab)}
        className={styles.content}
      >
        {activeTab === 'diagnostics' ? (
          <div style={{ padding: '8px', overflowY: 'auto', height: '100%' }}>
            {diagnostics.length === 0 ? (
              <EmptyState title="No diagnostics" description="No errors or warnings found." />
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {diagnostics.map((diag, i) => (
                  <li key={i} style={{ color: diag.level === 'error' ? 'red' : 'yellow', padding: '4px' }}>
                    [{diag.level.toUpperCase()}] {diag.message} (Net: {diag.networkId}, El: {diag.elementId})
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : activeTab === 'plcMonitor' ? (
          <IOPanel />
        ) : activeTab === 'alarms' ? (
          <AlarmPanel />
        ) : activeTab === 'modbus' ? (
          <ModbusMonitorPanel />
        ) : (
          <EmptyState
            title={t('bottom.placeholder.title')}
            description={t(`bottom.placeholder.${activeTab}`)}
          />
        )}
      </div>
    </Panel>
  );
}
